import { useCallback, useEffect, useState } from 'react';
import { useToggler } from '@cbhq/cds-common/hooks/useToggler';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { ThemeProvider } from '@cbhq/cds-web/system';

import { BuildQuestNavigation } from ':cloud/widgets/buildQuest/BuildQuestNavigation';
import { CenterTile } from ':cloud/widgets/buildQuest/CenterTile';
import { TILE_HEIGHT } from ':cloud/widgets/buildQuest/constants';
import { GameTile } from ':cloud/widgets/buildQuest/GameTile';
import { PlayerPanel } from ':cloud/widgets/buildQuest/PlayerPanel';
import { BuildQuestPrizes } from ':cloud/widgets/buildQuest/PrizesModal';
import { QuestionModal } from ':cloud/widgets/buildQuest/QuestionModal';

const padding = 100;
const mainWrapperPadding = { paddingTop: padding };
function getRowOffset(row: number) {
  return row * (TILE_HEIGHT - 48);
}
const totalRows = 4;

export function BuildQuestLanding(): React.ReactElement {
  const [visible, { toggleOn: togglePrizesModalOn, toggleOff: togglePrizesModalOff }] =
    useToggler();
  const [selectedQuestionId, setSelectedQuestionId] = useState('');
  const [
    isQuestionModalVisible,
    { toggleOn: toggleQuestionModalOn, toggleOff: toggleQuestionModalOff },
  ] = useToggler();

  // we'll set the IDs manually; it's more trouble than it's worth to programmatically lay out a hex grid
  // isUnlocked, isComplete, pointValue will be fetched from the backend

  const bodyElement = document.body;
  bodyElement.style.backgroundColor = 'rgb(10,11,13)'; // we don't have access to the theme provider here
  bodyElement.style.overflow = 'auto';

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('Coinbase Developer Platform (CDP) is building tools to bring the world onchain.');
    // eslint-disable-next-line no-console
    console.log("We're hiring! https://www.coinbase.com/careers/positions/developer-platform");
  }, []);

  const handleClose = useCallback(() => {
    togglePrizesModalOff();
    toggleQuestionModalOff();
    setSelectedQuestionId('');
  }, [toggleQuestionModalOff, togglePrizesModalOff]);

  const handleTilePress = useCallback(
    (tileId) => {
      setSelectedQuestionId(tileId);
      toggleQuestionModalOn();
    },
    [setSelectedQuestionId, toggleQuestionModalOn],
  );

  return (
    <ThemeProvider scale="large" spectrum="dark">
      <BuildQuestNavigation toggleOn={togglePrizesModalOn} />
      <VStack
        background="background"
        alignItems="center"
        width="100%"
        minHeight="100vh"
        style={mainWrapperPadding}
      >
        <VStack
          width="100%"
          alignItems="center"
          justifyContent="center"
          position="relative"
          minHeight={TILE_HEIGHT * totalRows - padding}
        >
          <HStack gap={2} position="absolute" top={getRowOffset(0)}>
            <GameTile
              id="1"
              isUnlocked
              isComplete
              pointValue={10}
              variant="beginner"
              onPress={handleTilePress}
            />
            <GameTile
              id="2"
              isUnlocked
              isComplete
              pointValue={10}
              variant="beginner"
              onPress={handleTilePress}
            />
          </HStack>
          {/* this is the center row */}
          <HStack gap={2} position="absolute" top={getRowOffset(1)}>
            <GameTile
              id="3"
              isUnlocked
              isComplete
              pointValue={100}
              variant="backend"
              onPress={handleTilePress}
            />
            <CenterTile completed /> {/* This one will always be complete if user is logged in */}
            <GameTile
              id="4"
              isUnlocked
              isComplete={false}
              pointValue={100}
              variant="frontend"
              onPress={handleTilePress}
            />
          </HStack>
          <HStack gap={2} position="absolute" top={getRowOffset(2)}>
            <GameTile
              id="5"
              isUnlocked
              isComplete={false}
              pointValue={300}
              variant="backend"
              onPress={handleTilePress}
            />
            <GameTile
              id="6"
              isUnlocked
              isComplete={false}
              pointValue={300}
              variant="frontend"
              onPress={handleTilePress}
            />
          </HStack>
          <HStack gap={2} position="absolute" top={getRowOffset(3)}>
            <GameTile
              id="7"
              isUnlocked
              isComplete={false}
              pointValue={500}
              variant="final"
              onPress={handleTilePress}
            />
          </HStack>
        </VStack>
      </VStack>

      <PlayerPanel />
      <BuildQuestPrizes visible={visible} handleClose={handleClose} />
      <QuestionModal
        visible={isQuestionModalVisible}
        handleClose={handleClose}
        questionId={selectedQuestionId}
      />
    </ThemeProvider>
  );
}
